/* tmc-microbiome-explainer.js — Home, "The microbiome, and your gut." (NW-100, issue #458).
   Markup and load state: sections/tmc-microbiome-explainer.liquid. The page is right before this runs; this adds:

   - The Area accordion. One Area open at a time; opening one closes the other, and opening the open one closes
     it. The section root's data-mx-open is the open Area's key ("" when none), which is all the human
     (snippets/tmc-explainer-figure.liquid) reads. The caption under the human names the open Area, or reads
     "07 AREAS · SELECT ONE" when none is. Below 768 the small human is moved into the open row's header.
   - The Key terms: three buttons that open and close on their own. Below 768 the footer's "Sources" button
     folds the full sources list the same way.
   - Links in. Any [data-tmc-explainer-open="<key>"] link on the page (the condition cards' "Learn more") scrolls
     here and opens that Area; a URL ending #explainer-<key> opens it on load. When the Area is hidden in the
     theme editor the link's own href (#explainer) still brings the visitor here.
   - Motion pause. The root carries data-mx-paused while it is off-screen or the tab is hidden, so the human's
     motion (assets/tmc-explainer-figure.css) stops when nobody can see it.
   - Idle tour (Jackson, 25 Sep 2026). While no Area is open, data-mx-tour steps from "intestine" through each shown
     Area in list order and back, and the human lights each one with a link from the gut. Opening an Area ends
     it; closing the open Area starts it again from the intestine. It never steps under reduced motion or while
     paused, so the figure then rests on the intestine. Steps are short (1.4 s on the intestine, 2 s per Area) and the figure moves from the
     start of each, because a visitor may look for under a second (Jackson, 25 Sep). The caption does not
     follow the tour (it is aria-live). */
(function () {
  'use strict';

  if (window.__tmcMx) return;
  window.__tmcMx = true;

  var still = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var phone = window.matchMedia ? window.matchMedia('(max-width: 767px)') : { matches: false };

  function init(root) {
    if (!root || root.__tmcMx) return;
    root.__tmcMx = true;

    var caption = root.querySelector('[data-mx-caption]');
    var idle = root.getAttribute('data-mx-idle') || '';

    function rows() { return [].slice.call(root.querySelectorAll('[data-mx-row]')); }
    function rowFor(key) { return rows().filter(function (r) { return r.getAttribute('data-mx-key') === key; })[0]; }

    function setRow(row, on) {
      row.setAttribute('data-open', on ? 'true' : 'false');
      var btn = row.querySelector('.tmc-mx__btn');
      if (btn) btn.setAttribute('aria-expanded', on ? 'true' : 'false');
    }

    /* Opens the Area with this key and closes the rest; an unknown or empty key closes them all. */
    var tourKeys = ['intestine'].concat((root.getAttribute('data-mx-tour-keys') || '').split(',').filter(Boolean));
    var tourI = 0;
    var tourT = null;
    function tourStop() { if (tourT) { clearTimeout(tourT); tourT = null; } }
    /* Keeps the tour in step with the section: off when an Area is open, resting when it may not move. */
    function tour() {
      if (root.getAttribute('data-mx-open')) {
        tourStop();
        root.removeAttribute('data-mx-tour');
        return;
      }
      if (!root.hasAttribute('data-mx-tour')) { tourI = 0; root.setAttribute('data-mx-tour', tourKeys[0]); }
      var run = !still.matches && !root.hasAttribute('data-mx-paused') && tourKeys.length > 1;
      if (!run) { tourStop(); return; }
      if (tourT) return;
      tourT = setTimeout(function () {
        tourT = null;
        tourI = (tourI + 1) % tourKeys.length;
        root.setAttribute('data-mx-tour', tourKeys[tourI]);
        tour();
      }, tourI === 0 ? 1400 : 2000);
    }

    function open(key) {
      var row = key ? rowFor(key) : null;
      rows().forEach(function (r) { setRow(r, r === row); });
      root.setAttribute('data-mx-open', row ? key : '');
      tour();
      if (caption) caption.textContent = row ? row.getAttribute('data-mx-caption') : idle;
      var mini = root.querySelector('[data-mx-mini]');
      var slot = row && row.querySelector('[data-mx-mini-slot]');
      if (mini && slot && mini.parentNode !== slot) slot.appendChild(mini);
      return row;
    }
    root.__mxOpen = open;

    root.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var btn = t.closest('.tmc-mx__btn');
      if (btn && root.contains(btn)) {
        var row = btn.closest('[data-mx-row]');
        open(row.getAttribute('data-open') === 'true' ? '' : row.getAttribute('data-mx-key'));
        return;
      }
      var src = t.closest('.tmc-mx__src-btn');
      if (src && root.contains(src)) {
        var wrap = src.closest('[data-mx-src]');
        var shown = wrap.getAttribute('data-open') !== 'true';
        wrap.setAttribute('data-open', shown ? 'true' : 'false');
        src.setAttribute('aria-expanded', shown ? 'true' : 'false');
        return;
      }
      var term = t.closest('.tmc-mx__term-btn');
      if (term && root.contains(term)) {
        var box = term.closest('[data-mx-term]');
        var on = box.getAttribute('data-open') !== 'true';
        box.setAttribute('data-open', on ? 'true' : 'false');
        term.setAttribute('aria-expanded', on ? 'true' : 'false');
      }
    });

    /* Motion pause: off-screen or a hidden tab. Without IntersectionObserver the human simply keeps its motion. */
    var visible = true;
    function pause() {
      if (!visible || document.hidden) root.setAttribute('data-mx-paused', '');
      else root.removeAttribute('data-mx-paused');
      tour();
    }
    if (still.addEventListener) still.addEventListener('change', tour);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[entries.length - 1].isIntersecting;
        pause();
      }).observe(root);
    }
    document.addEventListener('visibilitychange', pause);
    pause();

    var m = /^#explainer-([a-z]+)$/.exec(window.location.hash || '');
    if (m && rowFor(m[1])) reveal(m[1], false);
  }

  function rootEl() { return document.querySelector('[data-mx]'); }

  /* Opens an Area and brings it into view: the whole section from 768 (the human sits beside the list), the
     row itself on a phone (its own small human is in the header). Focus goes to the row's button. */
  function reveal(key, smooth) {
    var root = rootEl();
    if (!root || !root.__mxOpen) return false;
    var row = root.__mxOpen(key);
    if (!row) return false;
    var target = phone.matches ? row : root;
    if (target.scrollIntoView) target.scrollIntoView({ behavior: smooth && !still.matches ? 'smooth' : 'auto', block: 'start' });
    var btn = row.querySelector('.tmc-mx__btn');
    if (btn && btn.focus) btn.focus({ preventScroll: true });
    return true;
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    var link = t && t.closest && t.closest('[data-tmc-explainer-open]');
    if (!link) return;
    if (reveal(link.getAttribute('data-tmc-explainer-open'), true)) {
      e.preventDefault();
      if (window.history && history.replaceState) history.replaceState(null, '', '#explainer-' + link.getAttribute('data-tmc-explainer-open'));
    }
  });

  function initAll(scope) {
    [].forEach.call((scope || document).querySelectorAll('[data-mx]'), init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  /* Theme editor: selecting an Area block opens it. */
  document.addEventListener('shopify:block:select', function (e) {
    var row = e.target && e.target.closest && e.target.closest('[data-mx-row]');
    var root = row && row.closest('[data-mx]');
    if (root && root.__mxOpen) root.__mxOpen(row.getAttribute('data-mx-key'));
  });
})();
